import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import CommonModal from "components/common/CommonModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EnhancedTable from "components/common/EnhancedTable";
import { useSessionClass } from "contexts/SessionClassContext";
import { useAuth } from "contexts/AuthContext";
import { useMarkSheets } from "hooks/useMarkSheets";
import { useStudents } from "hooks/useStudents";
import { useSessions, SessionData } from "hooks/useSession";
import { useClasses } from "hooks/useClasses";
import { useStudentClassMapping } from "hooks/useStudentClassMapping";
import StudentPromotionFormModal from "components/Modals/StudentPromotionFormModal";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { MARKSHEET_TYPE_OPTIONS } from "components/enum/MarksheetTypeOptions";
import PramoteStudentFormModal from "components/Modals/pramoteStudentFormModal";
import { useTranslation } from "react-i18next";
import { useError } from "contexts/ErrorContext";
import SearchIcon from "@mui/icons-material/Search";
import { useTdData } from "hooks/useTdData";
import { getClassRank } from "components/enum/ClassOptions";

const PramoteStudentManagement: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [marksheetType, setMarksheetType] = useState("");
  const [status, setStatus] = useState("NOT_PROMOTED");
  const [selectedPromotionClassFilterId, setSelectedPromotionClassFilterId] = useState<number | "">("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [pramoteModalOpen, setPramoteModalOpen] = useState(false);
  const [pramoteStudentId, setPramoteStudentId] = useState<number | null>(null);
  const [pramoteStudentCurrentClassName, setPramoteStudentCurrentClassName] =
    useState<string | null>(null);
  const [
    pramoteStudentCurrentSessionName,
    setPramoteStudentCurrentSessionName,
  ] = useState<string | null>(null);
  const [pramoteMonthType, setPramoteMonthType] = useState<string | null>(null);
  const [pramoteStudentRollNo, setPramoteStudentRollNo] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [editPromotionModalOpen, setEditPromotionModalOpen] = useState(false);
  const [editingPromotionTrack, setEditingPromotionTrack] = useState<any>(null);
  const [confirmPromoteDialogOpen, setConfirmPromoteDialogOpen] = useState(false);
  const [studentToPromote, setStudentToPromote] = useState<any>(null);
  const [confirmDemoteDialogOpen, setConfirmDemoteDialogOpen] = useState(false);
  const [studentToDemote, setStudentToDemote] = useState<any>(null);

  const schoolId = user?.schoolId;
  const currentClassId = selectedClass?.id;
  const currentSessionId = selectedSession?.id;
  const currentSessionName = selectedSession?.name;

  // Fetch sessions and classes
  const { sessionsQuery } = useSessions(schoolId ?? undefined);
  const { classesQuery } = useClasses(schoolId ?? undefined);

  // Fetch TC data for current session and class to filter out students with TC data
  const { useTcData } = useTdData();
  const {
    data: tcDataList,
  } = useTcData(
    currentSessionId,
    currentClassId,
    schoolId ?? undefined,
    !!currentSessionId && !!currentClassId
  );

  // Function to calculate next session name (e.g., 2025-26 -> 2026-27)
  const getNextSessionName = (currentSessionName: string): string => {
    const match = currentSessionName.match(/(\d{4})-(\d{4})/);
    if (match) {
      const startYear = parseInt(match[1], 10);
      const endYear = parseInt(match[2], 10);
      return `${startYear + 1}-${endYear + 1}`;
    }
    return "";
  };

  // Find next session
  const nextSessionName = currentSessionName ? getNextSessionName(currentSessionName) : "";
  const nextSession = useMemo(() => {
    if (!nextSessionName || !sessionsQuery.data) return null;
    return sessionsQuery.data.find((s: SessionData) => s.name === nextSessionName) || null;
  }, [nextSessionName, sessionsQuery.data]);

  // Find next class
  const nextClass = useMemo(() => {
    if (!selectedClass || !classesQuery.data) return null;
    const currentClassRank = getClassRank(selectedClass.name);
    const nextClassRank = currentClassRank + 1;
    return classesQuery.data.find((cls: any) => {
      const classRank = getClassRank(cls.name);
      const sameSection = selectedClass.section ? cls.section === selectedClass.section : true;
      return classRank === nextClassRank && sameSection;
    }) || null;
  }, [selectedClass, classesQuery.data]);

  // Class options for dropdown
  // Show ALL sections of:
  // - Current class number (layout selected class)
  // - Next class number
  // Example: if current is 10th and we have 10th A, 10th C, and 11th A in school,
  // dropdown will show: 10th A, 10th C, 11th A.
  const classOptions = useMemo(() => {
    const options: Array<{ id: number; name: string; label: string }> = [];
    if (!selectedClass || !classesQuery.data) return options;

    const allClasses = classesQuery.data || [];
    const currentClassRank = getClassRank(selectedClass.name);
    if (currentClassRank === -100) return options;

    const nextClassRank = currentClassRank + 1;

    const addClassSections = (targetClassRank: number) => {
      allClasses
        .filter((cls: any) => {
          const clsRank = getClassRank(cls.name);
          return clsRank === targetClassRank;
        })
        .forEach((cls: any) => {
          options.push({
            id: cls.id,
            name: cls.name,
            label: `${cls.name}${cls.section ? ` - ${cls.section}` : ""}`,
          });
        });
    };

    // Add all sections of current class number
    addClassSections(currentClassRank);
    // Add all sections of next class number
    addClassSections(nextClassRank);

    return options;
  }, [selectedClass, classesQuery.data]);

  const {
    useGetPromoteTrackBySessionClass,
    useStudentById,
    revertPromotionMutation,
    usePromoteMapping,
  } = useStudents(false, schoolId ?? undefined);

  const { data: promoteMappings = [] } = usePromoteMapping(
    selectedClass?.name?.trim() || undefined
  );

  const showPromoteWithSubjects = useMemo(() => {
    const selectedClassName = selectedClass?.name?.trim().toLowerCase();
    if (!selectedClassName || !promoteMappings.length) return false;
    return promoteMappings.some(
      (mapping) =>
        mapping.fromClass?.trim().toLowerCase() === selectedClassName &&
        mapping.enableSubjectSelection === true
    );
  }, [selectedClass?.name, promoteMappings]);

  /** When the backend defines promotion mappings, limit the promotion-class filter to allowed target classes */
  const promotionClassOptions = useMemo(() => {
    if (!promoteMappings.length) return classOptions;
    const allowed = new Set(promoteMappings.map((m) => m.toClass.trim()));
    const filtered = classOptions.filter((o) => allowed.has(o.name.trim()));
    return filtered.length > 0 ? filtered : classOptions;
  }, [classOptions, promoteMappings]);


  // Only use the filter ID if it's a number, otherwise it's undefined
  const promotionClassId = typeof selectedPromotionClassFilterId === 'number' ? selectedPromotionClassFilterId : undefined;
  // Use next session ID if available
  const promotionSessionId = nextSession?.id;

  useEffect(() => {
    if (selectedClass && typeof selectedClass.name === "string") {
      const className = selectedClass.name.trim();
      if (className.startsWith("10th") || className.startsWith("12th")) {
        setMarksheetType("BOARD");
      } else {
        setMarksheetType("ANNUAL");
      }
    } else {
      setMarksheetType("");
    }
    // Reset promotion class filter when current class changes
    setSelectedPromotionClassFilterId("");
  }, [selectedClass]);



  // Fetch marksheets for PROMOTED status
  const { marksheetsQuery, useTcList } = useMarkSheets(
    false,
    false,
    currentClassId,
    currentSessionId
  );

  // Fetch promotion track data by sessionId, classId, and prevClass (for NOT_PROMOTED status)
  const {
    data: promoteTrackData,
    isLoading: isPromoteTrackLoading,
    error: promoteTrackError,
  } = useGetPromoteTrackBySessionClass(
    status !== "PROMOTED" ? promotionSessionId : undefined, // Only fetch when NOT_PROMOTED
    status !== "PROMOTED" ? promotionClassId : undefined, // Only fetch when NOT_PROMOTED
    status !== "PROMOTED" ? currentClassId : undefined // Only fetch when NOT_PROMOTED
  );

  // Fetch all students who have marksheets for the current class and session
  const { data: marksheetStudents, isLoading: isMarksheetStudentsLoading } = useTcList({
    schoolId: schoolId!,
    classId: (() => {
      const rawClassName = String(selectedClass?.name ?? "").trim();
      const isAllClass =
        (selectedClass?.id != null && Number(selectedClass.id) < 0) ||
        /\bALL\b$/i.test(rawClassName.replace(/-/g, " "));
      return isAllClass ? null : (currentClassId!);
    })(),
    className: (() => {
      const rawClassName = String(selectedClass?.name ?? "").trim();
      const isAllClass =
        (selectedClass?.id != null && Number(selectedClass.id) < 0) ||
        /\bALL\b$/i.test(rawClassName.replace(/-/g, " "));

      if (!isAllClass) return rawClassName;
      if (/-ALL$/i.test(rawClassName)) return rawClassName.replace(/-all$/i, "-ALL");
      if (/\s+ALL$/i.test(rawClassName)) return rawClassName.replace(/\s+ALL$/i, "-ALL");
      return rawClassName ? `${rawClassName}-ALL` : "ALL";
    })(),
    sessionId: currentSessionId!,
    marksheetType: marksheetType
  }, status === "NOT_PROMOTED" && !!schoolId && !!currentSessionId && !!marksheetType);

  // Check if 404 error (no data found)
  const is404Error = (promoteTrackError && (promoteTrackError as any)?.response?.status === 404);
  const hasNoData = status === "PROMOTED"
    ? !marksheetsQuery.isLoading && (!marksheetsQuery.data || marksheetsQuery.data.filter((m: any) =>
      m.classId === currentClassId && m.sessionId === currentSessionId && m.promote === "PROMOTED"
    ).length === 0)
    : !isMarksheetStudentsLoading && (!marksheetStudents || marksheetStudents.length === 0);

  // Get student IDs from promote track - filter only status: true and prevClass matches current class
  const approvedPromoteTracks = useMemo(() => {
    if (!promoteTrackData || !currentClassId) return [];
    return promoteTrackData.filter((track: any) =>
      track.status === true &&
      track.prevClass === currentClassId // Match prevClass with current selected class in layout
    );
  }, [promoteTrackData, currentClassId]);

  // For PROMOTED status: Fetch marksheets directly for current class and session
  const promotedMarksheets = useMemo(() => {
    if (status !== "PROMOTED" || !marksheetsQuery.data || !currentClassId || !currentSessionId) {
      return [];
    }

    // Filter marksheets by current selected class, current session, and both BOARD and ANNUAL types
    // Only include marksheets where promote === "PROMOTED"
    return marksheetsQuery.data.filter((m: any) =>
      m.classId === currentClassId &&
      m.sessionId === currentSessionId &&
      (m.marksheetType === "BOARD" || m.marksheetType === "ANNUAL") &&
      m.promote === "PROMOTED"
    );
  }, [status, marksheetsQuery.data, currentClassId, currentSessionId]);

  // Get student IDs from promoted marksheets
  const promotedStudentIds = useMemo(() => {
    return promotedMarksheets.map((m: any) => m.student?.id).filter((id: any): id is number => !!id);
  }, [promotedMarksheets]);

  const allStudentIds = useMemo(() => {
    const ids = new Set<number>();

    // Add IDs from promote track
    if (approvedPromoteTracks) {
      approvedPromoteTracks.forEach((track: any) => ids.add(track.studentId));
    }

    // Add IDs from marksheet students
    if (marksheetStudents) {
      marksheetStudents.forEach((ms: any) => ids.add(ms.studentId));
    }

    // Add IDs for PROMOTED status
    if (promotedStudentIds) {
      promotedStudentIds.forEach(id => ids.add(id));
    }

    return Array.from(ids);
  }, [approvedPromoteTracks, marksheetStudents, promotedStudentIds]);

  // Fetch individual students by their IDs
  const studentQueries = useQueries({
    queries: allStudentIds.map((studentId: number) => {
      return {
        queryKey: ["studentById", studentId],
        queryFn: async () => {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/students/${studentId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
            },
          });
          return response.data;
        },
        enabled: !!studentId,
        staleTime: 0,
      };
    }),
  });

  // Only consider loading if we have IDs to fetch
  const isStudentsLoading = allStudentIds.length > 0 && studentQueries.some(query => query.isLoading);




  // Combine student data with marksheet data
  const tcList: any[] = useMemo((): any[] => {
    // Create a map of studentId -> student data
    const studentMap = new Map<number, any>();
    studentQueries.forEach((query) => {
      if (query.data) {
        studentMap.set(query.data.id, query.data);
      }
    });

    // Get set of student IDs that have TC data (to exclude them from promotion list)
    const studentsWithTcData = new Set<number>();
    if (Array.isArray(tcDataList) && tcDataList.length > 0) {
      tcDataList.forEach((tc: any) => {
        const sId = tc.marksheet?.student?.id || tc.studentId;
        if (sId) {
          studentsWithTcData.add(Number(sId));
        }
      });
    }

    // Create a map of studentId -> promote track data
    const promoteTrackMap = new Map<number, any>();
    if (approvedPromoteTracks) {
      approvedPromoteTracks.forEach((track: any) => {
        promoteTrackMap.set(track.studentId, track);
      });
    }

    // For PROMOTED status: use marksheets directly
    if (status === "PROMOTED") {
      if (promotedMarksheets.length === 0) return [];

      return promotedMarksheets
        .map((marksheet: any) => {
          const studentId = marksheet.student?.id;
          if (!studentId || studentsWithTcData.has(studentId)) return null;

          const student = studentMap.get(studentId);

          return {
            studentId,
            student: student || marksheet.student,
            studentRollno: student?.rollNumber || marksheet.student?.rollNumber || "",
            studentName: student?.studentName || marksheet.student?.studentName || "",
            result: marksheet.result || "",
            className: selectedClass?.name || "",
            promote: marksheet.promote || "PROMOTED",
            session: selectedSession?.name || "",
            monthType: marksheet.monthType || "",
            promotionTrackData: null,
            promotionClassName: marksheet.promotedClassName || "",
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);
    }

    // For NOT_PROMOTED status: use marksheetStudents combined with promoteTrackData
    const items = (marksheetStudents || [])
      .map((ms: any) => {
        // Extract studentId — handle both flat (ms.studentId) and nested (ms.student?.id) shapes
        const sId: number | undefined = ms.studentId || ms.student?.id;
        if (!sId) return null;

        if (studentsWithTcData.has(sId)) return null;

        const student = studentMap.get(sId);
        const track = promoteTrackMap.get(sId);

        // Fetch result from annual or board marksheet specifically
        const marksheetResult = marksheetsQuery.data?.find((m: any) =>
          (m.student?.id === sId || m.studentId === sId) &&
          m.sessionId === currentSessionId &&
          (m.marksheetType === "ANNUAL" || m.marksheetType === "BOARD")
        )?.result;

        // Use promote status from marksheet students
        // Only show if it matches current status filter (NOT_PROMOTED)
        if (ms.promote === "PROMOTED") return null;

        return {
          studentId: sId,
          student: student, // May be undefined if still loading
          studentRollno: student?.rollNumber || ms.rollNumber || ms.student?.rollNumber || "",
          studentName: student?.studentName || ms.studentName || ms.student?.studentName || "",
          result: marksheetResult || ms.result || "",
          className: selectedClass?.name || "",
          promote: ms.promote || "NOT_PROMOTED",
          session: selectedSession?.name || "",
          monthType: ms.monthType || "",
          promotionTrackData: track || null,
          promotionClassName: track?.class?.name || ms.promotionClassName || "",
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // If a promotion class is selected, filter the NOT_PROMOTED list to only show students 
    // slated for that specific class (or who have filled a form for it)
    if (promotionClassId && items.length > 0) {
      return items.filter((item) => item.promotionTrackData?.classId === promotionClassId);
    }

    return items;
  }, [

    status,
    promotedMarksheets,
    marksheetStudents,
    approvedPromoteTracks,
    studentQueries.map(q => q.data),
    tcDataList,
    selectedClass?.name,
    selectedSession?.name,
    promotionClassId,
  ]);

  // Loading state:
  // - For NOT_PROMOTED: only show spinner while the marksheet list is fetching.
  //   Once it's done (even if empty), stop loading. Don't block on student detail queries.
  // - For PROMOTED: only show spinner while the marksheets query is in flight.
  // In both cases: stop loading immediately if we already have data to show (tcList not empty).
  const isLoading = tcList.length > 0
    ? false // Already have data — never show the full-page spinner
    : status === "PROMOTED"
      ? marksheetsQuery.isLoading
      : isMarksheetStudentsLoading;
  const error = promoteTrackError;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const { promoteStudentMutation } = useStudentClassMapping();
  const { showSuccess, showError } = useError();

  const handlePramoteStudent = (row: any) => {
    // row.studentId is always set (from ms.studentId || ms.student?.id during tcList build)
    // row.student?.id may be undefined if student details are still loading
    const studentId = row.studentId || row.student?.id;
    setPramoteStudentId(studentId ?? null);
    // Use the row's class name; fallback to selected class from layout
    setPramoteStudentCurrentClassName(row.className || selectedClass?.name || null);
    // Use the row's session name; fallback to selected session from layout
    setPramoteStudentCurrentSessionName(row.session || selectedSession?.name || null);
    setPramoteMonthType(row.monthType || null);
    setPramoteStudentRollNo(row.studentRollno || null);
    setPramoteModalOpen(true);
  };

  const handleEditPromotion = (row: any) => {
    // Use promotion track data from the row
    if (row.promotionTrackData) {
      setEditingPromotionTrack(row.promotionTrackData);
      setEditPromotionModalOpen(true);
    }
  };

  const handlePromoteStudent = (row: any) => {
    if (!row.promotionTrackData || !row.student?.id) return;
    setStudentToPromote(row);
    setConfirmPromoteDialogOpen(true);
    handleMenuClose();
  };

  const confirmPromoteStudent = async () => {
    if (!studentToPromote?.promotionTrackData || !studentToPromote?.student?.id) {
      setConfirmPromoteDialogOpen(false);
      setStudentToPromote(null);
      return;
    }

    const trackData = studentToPromote.promotionTrackData;
    const { studentId, classId, sessionId } = trackData;

    // Determine marksheet type based on layout selected class
    const className = selectedClass?.name || "";
    const marksheetType = className.startsWith("10th") || className.startsWith("12th") ? "BOARD" : "ANNUAL";

    promoteStudentMutation.mutate(
      {
        studentId,
        newClassId: classId,
        sessionId,
        marksheetType,
        schoolType: trackData.schoolType || "SAME",
      },
      {
        onSuccess: () => {
          showSuccess(t('student_promoted_successfully') || 'Student promoted successfully');
          setConfirmPromoteDialogOpen(false);
          setStudentToPromote(null);

          // Invalidate promotion track query to refresh the list
          queryClient.invalidateQueries({
            queryKey: ["promoteTrackBySessionClass", promotionSessionId, classId, currentClassId]
          });

          // Invalidate marksheet queries for all students
          queryClient.invalidateQueries({
            queryKey: ["marksheet", "studentId"]
          });

          // Invalidate student queries
          queryClient.invalidateQueries({
            queryKey: ["studentById"]
          });
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || t('error_promoting_student') || 'Failed to promote student. Please try again.';
          showError(errorMessage);
          setConfirmPromoteDialogOpen(false);
          setStudentToPromote(null);
        }
      }
    );
  };

  const handleDemoteStudent = (row: any) => {
    setStudentToDemote(row);
    setConfirmDemoteDialogOpen(true);
  };

  const confirmDemoteStudent = () => {
    if (!studentToDemote) {
      setConfirmDemoteDialogOpen(false);
      setStudentToDemote(null);
      return;
    }

    const studentId = studentToDemote.student?.id || studentToDemote.studentId;
    if (!studentId) {
      setConfirmDemoteDialogOpen(false);
      setStudentToDemote(null);
      return;
    }

    revertPromotionMutation.mutate(studentId, {
      onSuccess: () => {
        showSuccess(t("student_demoted_successfully") || "Student demoted successfully");
        setConfirmDemoteDialogOpen(false);
        setStudentToDemote(null);
        // Invalidate all relevant queries to refresh marksheets and get updated promote status
        queryClient.invalidateQueries({ queryKey: ["marksheets"] });
        queryClient.invalidateQueries({ queryKey: ["marksheet", "studentId"] });
        queryClient.invalidateQueries({ queryKey: ["studentById"] });
        queryClient.invalidateQueries({
          queryKey: ["promoteTrackBySessionClass", promotionSessionId, promotionClassId, currentClassId],
        });
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("error_demoting_student") ||
          "Failed to demote student. Please try again.";
        showError(errorMessage);
        setConfirmDemoteDialogOpen(false);
        setStudentToDemote(null);
      },
    });
  };



  const [isBulkPramote, setIsBulkPramote] = useState(false);

  const handleBulkPromote = () => {
    if (selectedStudents.size === 0) return;
    setIsBulkPramote(true);
    setPramoteModalOpen(true);
    setPramoteStudentCurrentClassName(selectedClass?.name || null);
    setPramoteStudentCurrentSessionName(selectedSession?.name || null);
  };

  // Table columns
  const baseColumns: Array<{
    id: string;
    label: string;
    minWidth: number;
    align?: "right" | "left" | "center";
    format?: (value: any) => string | React.ReactNode;
  }> = [
      { id: "studentRollno", label: t("roll_no"), minWidth: 100 },
      { id: "studentName", label: t("first_name"), minWidth: 130 },
      { id: "className", label: t("class"), minWidth: 100 },
      {
        id: "result",
        label: t("result") || "Result",
        minWidth: 100,
        format: (value: string) => value || "-" // Display "-" if result is empty
      },
      { id: "promote", label: t("status"), minWidth: 100 },
    ];

  // Filter data based on status and search
  const filteredData = useMemo(() => {
    if (!tcList || tcList.length === 0) return [];

    let data = tcList;

    // Apply status filter
    if (status) {
      data = data.filter((row: any) => row.promote === status);
    }

    // Apply search filter
    if (searchTerm) {
      data = data.filter((row: any) => {
        const studentName = (row.studentName || "").toLowerCase();
        const rollNo = (row.studentRollno || "").toLowerCase();
        const search = searchTerm.toLowerCase();
        return studentName.includes(search) || rollNo.includes(search);
      });
    }

    return data;
  }, [tcList, status, searchTerm]);

  // Row objects matching selectedStudents Set — needed for EnhancedTable selectedRows prop
  const selectedRowObjects = useMemo(() =>
    filteredData.filter((row: any) => {
      const id = row.studentId || row.student?.id;
      return id && selectedStudents.has(id);
    }),
    [filteredData, selectedStudents]
  );

  // Columns
  const columns = baseColumns;

  // Render actions for each row
  const renderActions = (row: any) => {
    const isPromoted = row.promote === "PROMOTED";
    if (status !== "PROMOTED" && !isPromoted) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handlePramoteStudent(row)}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 32 }}
          >
            {t("promote_student") || "Promote"}
          </Button>
        </Box>
      );
    }
    if (status === "PROMOTED" && isPromoted) {
      return (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => handleDemoteStudent(row)}
          disabled={revertPromotionMutation.isLoading}
        >
          {t("demote_student") || "Demote student"}
        </Button>
      );
    }
    return null;
  };

  // Info message logic
  let infoMessage = "";
  if (!schoolId) infoMessage = t("please_login_to_view_students");
  else if (!selectedClass)
    infoMessage = t("please_select_a_class_to_view_students");
  else if (!selectedSession)
    infoMessage = t("please_select_a_session_to_view_students");
  else if (!nextSession)
    infoMessage = t("please_create_next_session") || "Please create next session";
  else if (!status) infoMessage = t("please_select_a_status_to_view_students");

  return (
    <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
      {/* Header row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        {/* Left: Title + Info tooltip icon */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
            {t("promote_student_management")}
          </Typography>
          {promoteMappings.length > 0 && (
            <Tooltip
              arrow
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t("promotion_mappings") || "Promotion Mappings"}
                  </Typography>
                  {promoteMappings.map((m) => (
                    <Box key={m.id} component="div" sx={{ fontSize: "0.8rem" }}>
                      {m.fromClass} → {m.toClass}
                      {m.enableSubjectSelection
                        ? ` — ${t("subject_selection_enabled") || "Subject selection enabled"}`
                        : ""}
                    </Box>
                  ))}
                </Box>
              }
            >
              <IconButton size="small" color="info" sx={{ p: 0.25 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Right: Dropdowns */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {status !== "PROMOTED" && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t("promotion_class") || "Promotion Class"}</InputLabel>
              <Select
                value={selectedPromotionClassFilterId === "" ? "" : String(selectedPromotionClassFilterId)}
                label={t("promotion_class") || "Promotion Class"}
                onChange={(e: SelectChangeEvent) =>
                  setSelectedPromotionClassFilterId(e.target.value === "" ? "" : Number(e.target.value))
                }
                sx={{ height: 36 }}
              >
                <MenuItem value="">
                  <em>{t("select_class") || "Select Class"}</em>
                </MenuItem>
                {promotionClassOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("status")}</InputLabel>
            <Select
              value={status}
              label={t("status")}
              onChange={(e: SelectChangeEvent) => setStatus(e.target.value)}
              sx={{ height: 36 }}
            >
              {["PROMOTED", "NOT_PROMOTED"].map((option) => (
                <MenuItem key={option} value={option}>
                  {t(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label={t("marksheet_type")}
            value={
              marksheetType === "BOARD"
                ? t("board")
                : marksheetType === "ANNUAL"
                  ? t("annual")
                  : ""
            }
            InputProps={{ readOnly: true }}
            sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 36 } }}
          />
        </Box>
      </Box>

      {infoMessage ? (
        <Alert severity="info">{infoMessage}</Alert>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error && !is404Error ? (
        <Alert severity="error">{error.message}</Alert>
      ) : (is404Error || hasNoData || filteredData.length === 0) && !isLoading ? (
        <Alert severity="info">{t("no_student_promote_request_found") || "No Student Promote Request Found"}</Alert>
      ) : (
        <EnhancedTable
          columns={columns}
          data={filteredData}
          searchFields={[]}
          hideSearch
          rightActions={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder={t("search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
              />
              {status === "NOT_PROMOTED" && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBulkPromote}
                  disabled={selectedStudents.size === 0}
                  sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                >
                  {t("bulk_promote") || "Bulk Promote"}
                  {selectedStudents.size > 0 && ` (${selectedStudents.size})`}
                </Button>
              )}
            </Box>
          }
          actions={renderActions}
          selectable={status === "NOT_PROMOTED"}
          selectedRows={selectedRowObjects}
          onSelectionChange={(rows) => {
            setSelectedStudents(new Set<number>(
              rows.map((r: any) => r.studentId || r.student?.id).filter((id: any): id is number => !!id)
            ));
          }}
          getRowId={(row: any) => row.studentId || row.student?.id}
          onSelectAll={(checked) => {
            if (checked) {
              const allIds = filteredData
                .map((r: any) => r.studentId || r.student?.id)
                .filter((id: any): id is number => !!id);
              setSelectedStudents(new Set(allIds));
            } else {
              setSelectedStudents(new Set());
            }
          }}
          selectAllChecked={
            filteredData.length > 0 &&
            filteredData.every((row: any) => {
              const id = row.studentId || row.student?.id;
              return !!id && selectedStudents.has(id);
            })
          }
          selectAllIndeterminate={
            filteredData.some((row: any) => {
              const id = row.studentId || row.student?.id;
              return !!id && selectedStudents.has(id);
            }) &&
            !filteredData.every((row: any) => {
              const id = row.studentId || row.student?.id;
              return !!id && selectedStudents.has(id);
            })
          }
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              mb: 0,
              '& .MuiCardContent-root': {
                p: 0,
                '&:last-child': { pb: 0 },
              },
            },
          }}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {status === "PROMOTED" && (
          <>
            <MenuItem
              onClick={() => {
                if (menuRow) handleEditPromotion(menuRow);
              }}
            >
              {t("edit") || "Edit"}
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuRow) handlePromoteStudent(menuRow);
              }}
            >
              {t("promote_student")}
            </MenuItem>
          </>
        )}
        {status === "NOT_PROMOTED" && (
          <MenuItem
            onClick={() => {
              if (menuRow) handlePramoteStudent(menuRow);
              handleMenuClose();
            }}
          >
            {t("promote_student")}
          </MenuItem>
        )}
      </Menu>
      <PramoteStudentFormModal
        open={pramoteModalOpen}
        handleClose={() => {
          setPramoteModalOpen(false);
          setIsBulkPramote(false);
        }}
        studentId={!isBulkPramote ? (pramoteStudentId ?? 0) : undefined}
        studentIds={isBulkPramote ? Array.from(selectedStudents) : undefined}
        isBulk={isBulkPramote}
        marksheetType={marksheetType}
        monthType={pramoteMonthType || ""}
        studentRollNo={pramoteStudentRollNo || ""}
        currentClassName={pramoteStudentCurrentClassName || ""}
        currentSessionName={pramoteStudentCurrentSessionName || ""}
        showPromoteWithSubjects={showPromoteWithSubjects}
        onSuccess={() => {
          setPramoteModalOpen(false);
          setIsBulkPramote(false);
          setPramoteStudentId(null);
          setSelectedStudents(new Set());
          queryClient.invalidateQueries({ queryKey: ["marksheets"] });
          queryClient.invalidateQueries({ queryKey: ["tclist"] });
          queryClient.invalidateQueries({ queryKey: ["promoteTrackBySessionClass"] });
        }}
      />
      {editingPromotionTrack && (
        <StudentPromotionFormModal
          open={editPromotionModalOpen}
          handleClose={() => {
            setEditPromotionModalOpen(false);
            setEditingPromotionTrack(null);
          }}
          onSuccess={() => {
            setEditPromotionModalOpen(false);
            setEditingPromotionTrack(null);
            // Invalidate queries to refresh data
          }}
          initialData={editingPromotionTrack}
        />
      )}

      {/* Confirmation Dialog for Promote Student */}
      <CommonModal
        open={confirmPromoteDialogOpen}
        onClose={() => {
          setConfirmPromoteDialogOpen(false);
          setStudentToPromote(null);
        }}
        title={t("confirm_promotion") || "Confirm Promotion"}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={() => {
                setConfirmPromoteDialogOpen(false);
                setStudentToPromote(null);
              }}
              variant="outlined"
              disabled={promoteStudentMutation.isLoading}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={confirmPromoteStudent}
              variant="contained"
              color="primary"
              disabled={promoteStudentMutation.isLoading}
            >
              {promoteStudentMutation.isLoading ? <CircularProgress size={20} /> : (t("promote") || "Promote")}
            </Button>
          </>
        }
      >
        <Typography>
          {t("are_you_sure_want_to_promote_student") || "Are you sure you want to promote student?"}
        </Typography>
      </CommonModal>

      {/* Confirmation Dialog for Demote Student */}
      <CommonModal
        open={confirmDemoteDialogOpen}
        onClose={() => {
          setConfirmDemoteDialogOpen(false);
          setStudentToDemote(null);
        }}
        title={t("confirm_demotion") || "Confirm Demotion"}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={() => {
                setConfirmDemoteDialogOpen(false);
                setStudentToDemote(null);
              }}
              variant="outlined"
              disabled={revertPromotionMutation.isLoading}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={confirmDemoteStudent}
              variant="contained"
              color="secondary"
              disabled={revertPromotionMutation.isLoading}
            >
              {revertPromotionMutation.isLoading ? <CircularProgress size={20} /> : (t("demote") || "Demote")}
            </Button>
          </>
        }
      >
        <Typography>
          {t("are_you_sure_want_to_demote_student") || "Are you sure you want to demote student?"}
        </Typography>
      </CommonModal>
    </Paper>
  );
};

export default PramoteStudentManagement;
